import { create } from 'zustand'

export type SelfMedStatus = 'nu' | 'eerder'

export interface SelfMedItem {
  id: string
  title: string
  status: SelfMedStatus
  note: string
  createdAt: string
}

export interface MedNote {
  id: string
  date: string
  text: string
  createdAt: string
}

interface SelfMedicationState {
  // identifies whose device-local data is loaded, null = no user (nothing persists)
  userKey: string | null
  items: SelfMedItem[]
  // keyed by med key: brochure id for TMC meds, self-<id> for self-added items
  notes: Record<string, MedNote[]>
}

interface SelfMedicationActions {
  /** Load (or clear) the device-local data for the given user, e.g. on login / loginAs swap. */
  setUser: (userKey: string | null) => void
  addItem: (input: { title: string; status: SelfMedStatus; note: string }) => void
  removeItem: (id: string) => void
  addNote: (medKey: string, input: { date: string; text: string }) => void
  reset: () => void
}

export type SelfMedicationStore = SelfMedicationState & SelfMedicationActions

const STORAGE_PREFIX = 'tmc_self_medication'

// Per-user key so logout never has to destroy data and a token swap (loginAs)
// cannot surface another patient's items/notes.
const storageKey = (userKey: string): string => `${STORAGE_PREFIX}.${userKey}`

type PersistedState = Pick<SelfMedicationState, 'items' | 'notes'>

const load = (userKey: string): PersistedState => {
  try {
    const raw = localStorage.getItem(storageKey(userKey))
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersistedState>
      return { items: parsed.items ?? [], notes: parsed.notes ?? {} }
    }
  } catch {
    // corrupt or unavailable storage — start clean
  }
  return { items: [], notes: {} }
}

const save = (userKey: string | null, state: PersistedState): void => {
  if (userKey == null) return
  try {
    localStorage.setItem(storageKey(userKey), JSON.stringify(state))
  } catch {
    // storage full or unavailable — keep in-memory state
  }
}

const newId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

export const selfMedNoteKey = (itemId: string): string => `self-${itemId}`

// Device-local only (no backend endpoint). reset() clears in-memory state only:
// the patient is promised this data stays on the device between appointments.
export const useSelfMedicationStore = create<SelfMedicationStore>((set, get) => ({
  userKey: null,
  items: [],
  notes: {},

  setUser: (userKey) => {
    if (userKey === get().userKey) return
    if (userKey == null) {
      set({ userKey: null, items: [], notes: {} })
      return
    }
    set({ userKey, ...load(userKey) })
  },

  addItem: (input) => {
    const item: SelfMedItem = {
      id: newId(),
      title: input.title.trim(),
      status: input.status,
      note: input.note.trim(),
      createdAt: new Date().toISOString(),
    }
    const items = [item, ...get().items]
    set({ items })
    save(get().userKey, { items, notes: get().notes })
  },

  removeItem: (id) => {
    const items = get().items.filter((item) => item.id !== id)
    const notes = { ...get().notes }
    delete notes[selfMedNoteKey(id)]
    set({ items, notes })
    save(get().userKey, { items, notes })
  },

  addNote: (medKey, input) => {
    const note: MedNote = {
      id: newId(),
      date: input.date,
      text: input.text.trim(),
      createdAt: new Date().toISOString(),
    }
    const notes = { ...get().notes, [medKey]: [note, ...(get().notes[medKey] ?? [])] }
    set({ notes })
    save(get().userKey, { items: get().items, notes })
  },

  reset: () => {
    set({ userKey: null, items: [], notes: {} })
  },
}))
